// eventBus.ts

// Generic callback type used by subscribers.
// Each callback receives some data of type T.
type Callback<T = unknown> = (data: T) => void;

// A map where each event name stores a list of subscriber callbacks.
interface SubscribersMap {
  [event: string]: Callback<unknown>[];
}

class EventBus {
  // Stores all event subscribers, grouped by event name.
  private subscribers: SubscribersMap = {};

  /**
   * Subscribes a callback to a specific event.
   * Returns a cleanup function to unsubscribe later.
   */
  subscribe<T = unknown>(event: string, callback: Callback<T>): () => void {
    if (!this.subscribers[event]) {
      this.subscribers[event] = [];
    }

    // Cast needed because subscribers map is unknown[]
    this.subscribers[event].push(callback as Callback<unknown>);

    return () => {
      this.subscribers[event] = this.subscribers[event].filter(
        (cb) => cb !== callback
      );
    };
  }

  /**
   * Publishes an event and notifies all subscribers.
   */
  publish<T = unknown>(event: string, data: T): void {
    const list = this.subscribers[event];
    if (!list) return;

    list.forEach((callback) => {
      (callback as Callback<T>)(data);
    });
  }
}

// Export a single shared EventBus instance.
export const eventBus = new EventBus();
